import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { ScopeVersion } from '@core/model/scope-version'

import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { getPlainHttpParams } from '@app/shared/utils/utils'
import { environment } from '@envs/environment'
import { plainToInstance } from 'class-transformer'
import { map } from 'rxjs'
import { Observable } from 'rxjs/internal/Observable'
import { ScopePagination } from '../model/scope-pagination.model'
import { ScopeSection } from '../model/scope-section'
import { mapScopeDeliverable } from '../utils/map-scope-deliverable.utils'

export interface PaginatedDeliverablesRequest {
  page: number
  pageSize?: number
  version?: number
}

export const DELIVERABLES_PAGE_SIZE = 15

const DEFAULT_PAGINATED_DELIVERABLES_REQUEST: PaginatedDeliverablesRequest = {
  page: 0,
  pageSize: DELIVERABLES_PAGE_SIZE,
}

const DEFAULT_PAGINATED_SECTIONS_REQUEST: PaginatedDeliverablesRequest = {
  page: 0,
  pageSize: 15,
}

export interface PaginatedResponse<T> {
  content: T[]
  total: number
  page: number
  pageSize: number
}

@Injectable({
  providedIn: 'root',
})
export class ScopePaginatedService {
  private readonly BASE_API_URL: string = environment.scopeApiUrl

  constructor(private http: HttpClient) {}

  getScope(id: number | string): Observable<ScopeVersion> {
    return this.http.get<any>(`${this.BASE_API_URL}scopes/` + id).pipe(map((api) => plainToInstance(ScopeVersion, api)))
  }

  getDeliverables(
    scopeId: number,
    params: PaginatedDeliverablesRequest = DEFAULT_PAGINATED_DELIVERABLES_REQUEST
  ): Observable<ScopePagination<Deliverable>> {
    const url = `${this.BASE_API_URL}scopes/${scopeId}/deliverables`
    return this.http.get<ScopePagination<Deliverable>>(url, {
      params: getPlainHttpParams({ ...DEFAULT_PAGINATED_DELIVERABLES_REQUEST, ...params }),
    })
  }

  getSections(
    scopeId: number,
    params: PaginatedDeliverablesRequest = DEFAULT_PAGINATED_SECTIONS_REQUEST
  ): Observable<ScopePagination<ScopeSection>> {
    const url = `${this.BASE_API_URL}scopes/${scopeId}/sections`
    return this.http.get<ScopePagination<ScopeSection>>(url, {
      params: getPlainHttpParams({ ...DEFAULT_PAGINATED_SECTIONS_REQUEST, ...params }),
    })
  }

  getSectionDeliverables(
    scopeId: number,
    sectionId: number,
    params: PaginatedDeliverablesRequest = DEFAULT_PAGINATED_DELIVERABLES_REQUEST
  ): Observable<ScopePagination<Deliverable>> {
    const url = `${this.BASE_API_URL}scopes/${scopeId}/sections/${sectionId}/deliverables`
    return this.http
      .get<ScopePagination<Deliverable>>(url, {
        params: getPlainHttpParams({ ...DEFAULT_PAGINATED_DELIVERABLES_REQUEST, ...params }),
      })
      .pipe(
        map((response) => ({
          ...response,
          content: response.content.map((d) => mapScopeDeliverable(d, sectionId)),
        }))
      )
  }

  getScopeDeliverable(scopeId: number | string, deliverableId: number) {
    return this.http
      .get(`${this.BASE_API_URL}scopes/` + scopeId + `/deliverables/` + deliverableId)
      .pipe(map((api) => plainToInstance(Deliverable, api)))
  }

  getScopeSection(scopeId: number | string, sectionId: number) {
    return this.http
      .get(`${this.BASE_API_URL}scopes/` + scopeId + `/sections/` + sectionId)
      .pipe(map((api) => plainToInstance(ScopeSection, api)))
  }
}
